import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, Subject } from 'rxjs';
import { DateService } from '@/common/date.service';
import {
	Selected,
	Month,
	RoomKind,
	DateRange,
	DialogData,
	ProductKind,
	Colors,
	StateKind,
	ReservationOffer,
	Room,
	AvailabilityKind,
} from './accommodations.model';
import { LoaderService } from '@/common/loader/loader.service';
import { Data2Service } from '@/common/data2.service';
import { DataBagService } from '@/common/data-bag.service';
import { InfoboxService } from '@/common/infobox/infobox.service';
import { RoomAvailabilityState, RoomAvailabilityStateToPatch } from '@/reservation/reservation.model';
import { TransportKind } from '@/common/enums/TransportKind';
import { RoomType } from '@/common/enums/RoomType';
import { Status } from '@/common/enums/Status';
import { NoNames } from '@/common/enums/NoName';
import { Product } from './accommodations.model';

export interface ReservationMoveData {
	type: 'move' | 'dok' | 'accommodate';
	reservation?: any;
	capacities?: any;
	previousRoom?: any;
}

@Injectable({
	providedIn: 'root',
})
export class AccommodationsService {
	selected = new BehaviorSubject<Selected>({
		date: '',
		destination: {
			id: null,
			name: 'Kierunek',
		},
		products: [],
	});
	data = {
		destinations: [],
		products: [],
		productsSelected: [],
		roomTypes: [],
		nonames: [],
		occupancy: [],
		withoutRooms: [],
	};
	show = {
		noname: false,
		terms: false,
		subrooms: false,
	};
	roomProductMap = [];
	roomSlaveProductMap = [];
	reservationMove: ReservationMoveData | null = null;

	divStyles: Partial<CSSStyleDeclaration>;
	private stateToMove: RoomAvailabilityStateToPatch;
	private movingBagReservation = false;
	private accommodatingBagReservation = false;

	triggerRefreshingWithoutRooms$: Subject<void> = new Subject();
	triggerRefreshingView$: Subject<void> = new Subject();
	private readonly gridHeightPx = 40;

	constructor(
		private readonly dateService: DateService,
		private readonly route: ActivatedRoute,
		private readonly router: Router,
		private readonly loader: LoaderService,
		private readonly erp: Data2Service,
		private readonly bag: DataBagService,
		private readonly infobox: InfoboxService,
	) {}

	selectedSet<T>(key: string, value: T): void {
		this.selected.next({
			...this.selected.getValue(),
			[key]: value,
		});
	}

	queryParmSet(key: string, value: string | number): void {
		const queryParams = { ...this.route.snapshot.queryParams };
		queryParams[key] = value;
		this.router.navigate([], {
			relativeTo: this.route,
			queryParams: queryParams,
			queryParamsHandling: 'merge',
		});
	}

	productsSelectedRefresh(): void {
		const selected = this.selected.getValue().products;
		this.data.productsSelected = this.data.products.filter((obj) => selected.includes(obj.id));
	}

	generateMonths(): Month[] {
		const now = new Date(this.selected.getValue().date);
		let firstDay;
		const result: Month[] = [];
		for (let addMonths = 0; addMonths < 3; addMonths++) {
			firstDay = new Date(now.getFullYear(), now.getMonth() + addMonths, 1);
			result.push({
				name: firstDay.toLocaleString('default', { month: 'long' }) + ' ' + firstDay.getFullYear(),
				from: 1,
				days: this.dateService.daysInMonth(firstDay),
			});
		}
		result[0].from = now.getDate();
		return result;
	}

	dateMove(days: number): void {
		const queryParams = { ...this.route.snapshot.queryParams };
		queryParams.date = this.dateService.daysAdd(days, this.selected.getValue().date);
		this.router.navigate([], {
			relativeTo: this.route,
			queryParams: queryParams,
			queryParamsHandling: 'merge',
		});
	}

	parseAccomodations(results): void {
		this.roomProductMap = [];
		results.forEach((element) => {
			const product = this.data.products.find((obj) => obj.id == element.id);
			if (!product) {
				return;
			}
			product.rooms = element.rooms.filter((obj) => obj.isActive === true);
			product.rooms.forEach((room, index) => {
				const position = index + 1;
				room.availabilities = [];
				room.name = `${position}. ${room.roomNumber} (${room.roomType.code}) p: ${room.level} w: ${room?.view}`;
				room.occupancy = this.roomCapacityOccupancy(element.id, room.id);
				room.maxOverlappingStates = 0;
				this.roomProductMap.push(room);
				room.roomSlaves.forEach((roomSlave) => {
					roomSlave.availabilities = [];
					this.roomProductMap.push(roomSlave);
				});
			});
		});
		this.parseOccupancy();
		this.productsSelectedRefresh();
	}

	cleanAvailabilities() {
		this.roomProductMap.forEach((element) => {
			element.availabilities = [];
		});
	}

	parseAvailabilities(results, idName: string, type: string): void {
		results?.forEach((element) => {
			const room = this.roomProductMap.find((obj) => obj.id == element[idName]);
			if (!room) {
				return;
			}
			element.istourreservationSet?.forEach((reservation) => {
				reservation.guarantee = reservation.roomGuarantee != '';
			});
			const [left, width] = this.determineDivGeometry(element.dateRange);
			let zindex = 0;
			let height = '32px';
			let background = '';
			switch (type) {
				case AvailabilityKind.AVAILABILITY:
					background = this.availabilityBackground(element.availabilityType);
					zindex = 10;
					height = '100%';
					break;
				case AvailabilityKind.STATE:
					let kindReservation =
						element.istourreservationSet[0]?.kind + element.istourreservationSet[0]?.status;
					if (
						element.istourreservationSet[0]?.kind === RoomKind.NAME_OPTION ||
						element.istourreservationSet[0]?.kind === RoomKind.NO_NAME_OPTION
					) {
						kindReservation = 1;
					}
					background = this.availabilityStateBackground(element, kindReservation);
					if (element.state === 'R') {
						element.stateLabel = this.availabilityStateLable(element);
					} else {
						element.stateLabel = element.comment;
					}
					zindex = 20;

					if (!element.istourreservationSet.length) {
						this.updateIconBAG(element);
						break;
					}
					if (element.istourreservationSet[0]?.extendedStay === true) {
						element.extendedStay = 1;
					}
					if (element.istourreservationSet[0]?.ownTransport === true) {
						element.ownTransport = 1;
					}
					if (element.istourreservationSet[0]?.roomGuarantee != '') {
						element.roomGuarantee = 1;
					}
					if (element.istourreservationSet[0]?.viewGuarantee === true) {
						element.viewGuarantee = 1;
					}
					// else {
					// 	element.extendedStay = 0;
					// }

					break;
			}
			element.styles = {
				'left.px': left,
				'width.px': width,
				'z-index': zindex,
				height: height,
				background: background,
			};

			if (type === AvailabilityKind.STATE) {
				const overlappingStatesForDate = this.countOverlappingReservationStates(element, room.availabilities);
				if (room.maxOverlappingStates < overlappingStatesForDate) {
					room.maxOverlappingStates = overlappingStatesForDate;
				}

				element.styles['top.px'] = overlappingStatesForDate * this.gridHeightPx;
				room.styles = {
					height: `${(room.maxOverlappingStates + 1) * this.gridHeightPx}px`,
				};
			}

			room.availabilities.push(element);
		});
	}

	private availabilityStateBackground(element: RoomAvailabilityState, kindReservation: number): string {
		switch (element.state) {
			case StateKind.LOCKED:
				return Colors.BEIGE;
			case StateKind.SELL_BLOCKED:
				return Colors.PURPLE;
			case StateKind.RESERVED:
				if (element.istourreservationSet.length) {
					return this.availabilityRoomBackground(kindReservation);
				}
				return this.availabilityRoomBackgroundBAG(element);
		}
		return '';
	}
	private availabilityRoomBackgroundBAG(element) {
		const gradientColors = [];
		const isOfferOption: boolean = element.reservationofferSet[0]?.status === Status.OPTION;
		if (element.reservationofferSet[0]?.roomType.code === RoomType.ACCOMMODATE_TO_OCCUPIED_ROOM) {
			gradientColors.push(Colors.PINK);
		}
		if (NoNames.includes(element.reservationofferSet[0]?.product.name)) {
			gradientColors.push(isOfferOption ? Colors.LIGHT_BLUE : Colors.BLUE);
		}
		if (element?.product?.productKind.name === ProductKind.ROUND_TRIP) {
			gradientColors.push(isOfferOption ? Colors.LIGHT_GREEN : Colors.GREEN);
		}
		if (gradientColors.length === 1) {
			return gradientColors[0];
		} else if (gradientColors.length > 1) {
			return this.generateGradientString(gradientColors);
		}

		switch (element.product.productKind.name) {
			case ProductKind.TRIP:
				return isOfferOption ? Colors.YELLOW : Colors.ORANGE;
			case ProductKind.NO_NAME:
				return Colors.BLUE;
		}
	}

	private generateGradientString(colors: string[]): string {
		const angle = '135deg';
		const colorStops = colors
			.flatMap((color, index) => {
				const percentageFrom = (index * 100) / colors.length;
				const percentageTo = ((index + 1) * 100) / colors.length;
				return [`${color} ${percentageFrom}%, ${color} ${percentageTo}%`];
			})
			.join(', ');
		return `linear-gradient(${angle}, ${colorStops})`;
	}

	private availabilityRoomBackground(kindReservation: number) {
		switch (kindReservation) {
			case 1: // Name opcja
				return Colors.PINK;
			case 3: // Name opcja
				return Colors.YELLOW;
			case 6: // Name potwierdzona
				return Colors.ORANGE;
			case 4: // No-name opcja
				return Colors.LIGHT_BLUE;
			case 7: // No-name potwierdzona
				return Colors.BLUE;
			case 5: // Objazdówka opcja
				return Colors.LIGHT_GREEN;
			case 8: // Objazdówka potwierdzona
				return Colors.GREEN;
			default:
				return Colors.RED;
		}
	}

	private availabilityBackground(state: string): string {
		switch (state) {
			case 'A': // Allotment
				return 'rgba(0, 0, 255, 0.2)';
			case 'G': // Gwarancja
				return 'rgba(0, 255, 0, 0.2)';
		}
		return 'rgba(255, 0, 0, 0.4)';
	}

	private availabilityStateLable(element) {
		if (element.istourreservationSet.length) {
			return (
				element.istourreservationSet[0]?.isTourNumber +
				' ' +
				element.istourreservationSet[0]?.reservationofferSet[0]?.roomCode +
				' (' +
				element.istourreservationSet[0]?.personCount +
				') ' +
				element.istourreservationSet[0]?.roomOrder +
				'/' +
				element.istourreservationSet[0]?.roomCount
			);
		} else {
			const personCount: number = element?.offerSlot.length;
			return `${element.reservation[0] ?? 'brak'} ${
				element.reservationofferSet[0]?.roomType?.code
			} (${personCount})`;
		}
	}

	private pixelsBetweenDates(date1: string, date2: string): number {
		return this.dateService.daysBetween(date1, date2) * 20;
	}

	filterBy<T>(objectToFilter: T[], filterValue: string, filterBy: string): T[] {
		return objectToFilter
			.filter((item) => item[filterBy].toLocaleLowerCase().includes(filterValue.toLocaleLowerCase()))
			.slice();
	}

	reservationMoveHere(room: RoomAvailabilityState, product: Product): void {
		if (this.movingBagReservation) {
			this.changeRoomToBAG(room, product);
		} else if (this.accommodatingBagReservation) {
			this.addStateToOffer(room);
		} else {
			this.accommodateCapacite(room.id, this.reservationMove.reservation.businessCasesCapacities);
		}
		this.reservationMove = null;
	}

	accommodateCapacite(room: number, capacitie: number): Promise<any> {
		return new Promise((resolve) => {
			this.loader.show('accommodateCapacite');
			this.erp.command(
				`/Bag/BagIstourAccomodation/`,
				{
					cmd: 'accomodate',
					room: room,
					capacitie: capacitie,
				},
				(result, error) => {
					if (result.data.kind === 'ERROR') {
						this.loader.hide('accommodateCapacite');
						this.infobox.error('Pokój zajęty');
						return;
					}
					this.bag.get(
						`/product/room/availability_state/` + result.data.results[0].id + `/`,
						(result, error) => {
							this.parseAvailabilities([result], 'room', AvailabilityKind.STATE);
						},
					);
					this.loadReservationWithOutRoom();
					this.loader.hide('accommodateCapacite');
					resolve(result);
				},
			);
		});
	}
	accommodateCapaciteAfterDok(state: number): Promise<any> {
		return new Promise((resolve) => {
			this.loader.show('accommodateCapacite');
			this.bag.get(`/product/room/availability_state/` + state + `/`, (result, error) => {
				this.parseAvailabilities([result], 'room', AvailabilityKind.STATE);
			});
		});
	}

	parseOccupancy() {
		this.data.products.forEach((element) => {
			element.occupancy = this.productCapacityOccupancy(element.id);
		});
	}

	productCapacityOccupancy(product) {
		const capacityOccupancy = this.data.occupancy.find((obj) => obj.id === product);
		return capacityOccupancy?.capacityOccupancy;
	}

	roomCapacityOccupancy(productId: number, roomId: number) {
		const capacityOccupancy = this.data.occupancy.find((obj) => obj.id === productId);

		if (!capacityOccupancy || !capacityOccupancy.rooms || !capacityOccupancy.rooms.length) {
			return 0;
		}

		const roomCapacityOccupancy = capacityOccupancy.rooms.find((obj) => obj.roomId === roomId);

		return roomCapacityOccupancy ? roomCapacityOccupancy.capacityOccupancy : 0;
	}

	loadReservationWithOutRoom() {
		this.data.withoutRooms = [];
		const productsIn = this.selected.getValue().products.join(',');
		let products = productsIn;
		if (this.selected.getValue().destination.id === 11) {
			products = environment.nonameIdsHR + ',' + productsIn;
		}
		if (this.selected.getValue().destination.id === 12) {
			products = environment.nonameIdsMO + ',' + productsIn;
		}

		this.erp.command(`/Bag/BagIstourAccomodation/`, { cmd: 'withOut', products: products }, (result, error) => {
			if (error) {
				return;
			}
			this.data.withoutRooms = result.data.map((obj) => {
				obj.status = obj.status == 6 ? 'OK' : 'OP';
				obj.roomGuarantee = obj.roomGuarantee != '' ? obj.roomGuarantee : '';
				obj.viewGuarantee = obj.viewGuarantee != 0 ? obj.viewGuarantee : '';
				obj.guarantee = obj.roomGuarantee + obj.viewGuarantee;
				return obj;
			});
		});
	}

	private determineDivGeometry(dateRange: DateRange) {
		let left = this.pixelsBetweenDates(this.selected.getValue().date, dateRange.lower);
		let width = 0;
		if (left < 0) {
			left = 0;
			width = this.pixelsBetweenDates(this.selected.getValue().date, dateRange.upper);
		} else {
			width = this.pixelsBetweenDates(dateRange.lower, dateRange.upper);
		}
		return [left, width];
	}

	changeRoomBAG(data: DialogData) {
		const [left, width] = this.determineDivGeometry(data.dateRange);
		this.divStyles = {
			left: `${left}px`,
			width: `${width}px`,
			zIndex: '999',
		};
		this.reservationMove = {
			type: 'move',
		};
		const { id, room, dateRange, state, comment, product } = data;

		this.stateToMove = { id, room, dateRange, state, comment, destinationProduct: product };

		this.movingBagReservation = true;
	}

	changeRoomToBAG(room: RoomAvailabilityState, product: Product): void {
		this.loader.show('changeRoomToBAG');

		this.stateToMove.room = room.id;
		this.bag.patch(
			`/product/room/availability_state/${this.stateToMove.id}/`,
			this.stateToMove,
			(result, error) => {
				this.loader.hide('changeRoomToBAG');

				if (error) {
					return;
				}
				this.roomProductMap.forEach((element) => {
					element.availabilities = element.availabilities.filter((obj) => this.stateToMove.id !== obj.id);
				});

				this.movingBagReservation = false;
				this.triggerRefreshingView$.next();
			},
		);
	}

	updateIconBAG(element: RoomAvailabilityState) {
		if (element.reservationofferSet.some((item) => item.isOfferCombined)) {
			element.extendedStay = 1;
		}
		if (element.reservationofferSet.some((item) => item.transportKind == TransportKind.WITHOUT)) {
			element.ownTransport = 1;
		}
		if (element.reservationofferSet.some((item) => item.transportKind == TransportKind.FLIGHT)) {
			element.transportPlane = 1;
		}
		if (element.offerSlot.some((item) => item.roomGuarantee)) {
			element.roomGuarantee = 1;
		}
		if (element.offerSlot.some((item) => item.viewGuarantee)) {
			element.viewGuarantee = 1;
		}
	}

	removeStateFromOffer(state: RoomAvailabilityState) {
		this.loader.show();
		this.bag.del(`/product/room/availability_state/${state.id}/`, (result, error) => {
			this.loader.hide();
			if (error) {
				return;
			}
			this.triggerRefreshingWithoutRooms$.next();
			this.triggerRefreshingView$.next();
		});
	}

	startAddingStateToOffer(offer: ReservationOffer) {
		const dateRange: DateRange = {
			lower: offer.dateFrom,
			upper: offer.dateTo,
			bounds: '[]',
		};
		const [left, width] = this.determineDivGeometry(dateRange);
		this.divStyles = {
			left: `${left}px`,
			width: `${width}px`,
		};
		this.reservationMove = {
			type: 'move',
			reservation: offer.id,
		};
		this.accommodatingBagReservation = true;
	}

	addStateToOffer(room: Room) {
		const payload = {
			room: room.id,
		};
		this.loader.show();
		this.bag.post(
			`/reservation/offer/${this.reservationMove.reservation}/reserve_room/`,
			payload,
			(result, error) => {
				this.loader.hide();
				if (error) {
					return;
				}
				this.triggerRefreshingWithoutRooms$.next();
				this.triggerRefreshingView$.next();
			},
		);
	}

	countOverlappingReservationStates(state: RoomAvailabilityState, states: RoomAvailabilityState[]): number {
		const stateLower = new Date(state.dateRange.lower).getTime();
		const stateUpper = new Date(state.dateRange.upper).getTime();

		const overlappingStates = states.filter((otherState) => {
			const otherLower = new Date(otherState.dateRange.lower).getTime();
			const otherUpper = new Date(otherState.dateRange.upper).getTime();

			return stateLower < otherUpper && stateUpper > otherLower && otherState.state === StateKind.RESERVED;
		});

		return overlappingStates.length;
	}
}
